import { Set as ISet, List, Record } from '@gitbook/util-immutable';

import { MODEL_TYPES } from '../constants';
import { isBlockRangeSelection, isTextRangeSelection } from '../utils';
import type { Block } from './block';
import type { BlockRange } from './block-range';
import { Change, type ChangeFlags } from './change';
import { CORE_SCHEMA } from './core-schema';
import { Document } from './document';
import { History } from './history';
import type { Inline } from './inline';
import type { Mark } from './mark';
import { assertText } from './node';
import { Schema } from './schema';
import type { Text } from './text';
import { TextRange } from './text-range';

export type SelectionRange = TextRange | BlockRange;

export interface ValueProperties {
    document: Document;
    history: History;
    schema: Schema;
    selection: SelectionRange;
    decorations: ISet<TextRange>;
}

export type ValueCreateProps =
    | Value
    | {
          document?: Document;
          selection?: SelectionRange;
          schema?: Schema;
          history?: History;
      };

/*
 * Immutable state representing the value of the editor.
 */
export class Value extends Record<ValueProperties>({
    document: Document.create({
        // Initialize with a constant key to avoid errors in environments like Cloudflare Workers where
        // the use of crypto or random is not allowed outside a request handler.
        key: 'document_key',
    }),
    history: History.create(),
    schema: Schema.create(),
    selection: TextRange.create(),
    decorations: ISet.create<TextRange>(),
}) {
    // Record properties
    public declare readonly schema: Schema;
    public declare readonly selection: SelectionRange;
    public declare readonly history: History;
    public declare readonly document: Document;
    public declare readonly decorations: ISet<TextRange>;

    /*
     * Object.
     */

    get object(): 'value' {
        return 'value';
    }

    /*
     * Are there undoable events?
     */

    get hasUndos(): boolean {
        return this.history.undos.size > 0;
    }

    /*
     * Are there redoable events?
     */

    get hasRedos(): boolean {
        return this.history.redos.size > 0;
    }

    /*
     * Is the current selection blurred?
     */

    get isBlurred(): boolean {
        return this.selection.isBlurred;
    }

    /*
     * Is the current selection focused?
     */
    get isFocused(): boolean {
        return !!this.selection.isFocused;
    }

    /*
     * Is the current selection collapsed?
     */
    get isCollapsed(): boolean {
        return this.selection.isCollapsed;
    }

    /*
     * Is the current selection expanded?
     */

    get isExpanded(): boolean {
        return this.selection.isExpanded;
    }

    /*
     * Is the current selection backward?
     */
    get isBackward(): boolean {
        if (isBlockRangeSelection(this.selection)) {
            return false;
        }
        return !!this.selection.isBackward;
    }

    /*
     * Is the current selection forward?
     */
    get isForward(): boolean {
        if (isBlockRangeSelection(this.selection)) {
            return false;
        }
        return this.selection.isForward;
    }

    /*
     * Get the current start key.
     */
    get startKey(): string | null {
        if (isBlockRangeSelection(this.selection)) {
            return null;
        }
        return this.selection.startKey;
    }

    /*
     * Get the current end key.
     */
    get endKey(): string | null {
        if (isBlockRangeSelection(this.selection)) {
            return null;
        }
        return this.selection.endKey;
    }

    /*
     * Get the current start offset.
     */
    get startOffset(): number {
        if (isBlockRangeSelection(this.selection)) {
            return Number.NaN;
        }
        return this.selection.startOffset;
    }

    /*
     * Get the current end offset.
     */
    get endOffset(): number {
        if (isBlockRangeSelection(this.selection)) {
            return Number.NaN;
        }
        return this.selection.endOffset;
    }

    /*
     * Get the current anchor key.
     */
    get anchorKey(): string | null {
        if (isBlockRangeSelection(this.selection)) {
            return null;
        }
        return this.selection.anchorKey;
    }

    /*
     * Get the current focus key.
     */
    get focusKey(): string | null {
        if (isBlockRangeSelection(this.selection)) {
            return null;
        }
        return this.selection.focusKey;
    }

    /*
     * Get the current anchor offset.
     */
    get anchorOffset(): number {
        if (isBlockRangeSelection(this.selection)) {
            return Number.NaN;
        }
        return this.selection.anchorOffset;
    }

    /*
     * Get the current focus offset.
     */
    get focusOffset(): number {
        if (isBlockRangeSelection(this.selection)) {
            return Number.NaN;
        }
        return this.selection.focusOffset;
    }

    /**
     * Get the selection as a TextRange or throw if it's a BlockRange.
     */
    get textSelection(): TextRange {
        if (isBlockRangeSelection(this.selection)) {
            throw new Error('Cannot get a TextRange from a BlockRange selection');
        }
        return this.selection;
    }

    get isSelectionUnset() {
        return isTextRangeSelection(this.selection) && this.selection.isUnset;
    }

    /*
     * Get the current start text node's closest block parent.
     */
    get startBlock(): Block | null {
        return this.startKey ? this.document.getClosestBlock(this.startKey) : null;
    }

    /*
     * Get the current end text node's closest block parent.
     */
    get endBlock(): Block | null {
        return this.endKey ? this.document.getClosestBlock(this.endKey) : null;
    }

    /*
     * Get the current anchor text node's closest block parent.
     */
    get anchorBlock(): Block | null {
        if (isBlockRangeSelection(this.selection)) {
            const { anchorBlock } = this.selection;
            if (!anchorBlock) {
                return null;
            }
            return this.document.getBlocks().find((block) => block.key === anchorBlock) ?? null;
        }
        return this.anchorKey ? this.document.getClosestBlock(this.anchorKey) : null;
    }

    /*
     * Get the current focus text node's closest block parent.
     */
    get focusBlock(): Block | null {
        if (isBlockRangeSelection(this.selection)) {
            const { focusBlock } = this.selection;
            if (!focusBlock) {
                return null;
            }
            return this.document.getBlocks().find((block) => block.key === focusBlock) ?? null;
        }
        return this.focusKey ? this.document.getClosestBlock(this.focusKey) : null;
    }

    /*
     * Get the current start text node's closest inline parent.
     */
    get startInline(): Inline | null {
        return this.startKey ? this.document.getClosestInline(this.startKey) : null;
    }

    /*
     * Get the current end text node's closest inline parent.
     */
    get endInline(): Inline | null {
        return this.endKey ? this.document.getClosestInline(this.endKey) : null;
    }

    /*
     * Get the current anchor text node's closest inline parent.
     */

    get anchorInline(): Inline | null {
        return this.anchorKey ? this.document.getClosestInline(this.anchorKey) : null;
    }

    /*
     * Get the current focus text node's closest inline parent.
     */
    get focusInline(): Inline | null {
        return this.focusKey ? this.document.getClosestInline(this.focusKey) : null;
    }

    /*
     * Get the current start text node.
     */
    get startText(): Text | null {
        const result = this.startKey && this.document.getDescendant(this.startKey);
        return result ? assertText(result) : null;
    }

    /*
     * Get the current end node.
     */
    get endText(): Text | null {
        const result = this.endKey && this.document.getDescendant(this.endKey);
        return result ? assertText(result) : null;
    }

    /*
     * Get the current anchor node.
     */
    get anchorText(): Text | null {
        const result = this.anchorKey && this.document.getDescendant(this.anchorKey);
        return result ? assertText(result) : null;
    }

    /*
     * Get the current focus node.
     */
    get focusText(): Text | null {
        const result = this.focusKey && this.document.getDescendant(this.focusKey);
        return result ? assertText(result) : null;
    }

    /*
     * Get the next block node.
     */
    get nextBlock(): Text | null {
        const result = this.endKey && this.document.getNextBlock(this.endKey);
        return result ? assertText(result) : null;
    }

    /*
     * Get the previous block node.
     */
    get previousBlock(): Text | null {
        const result = this.startKey && this.document.getPreviousBlock(this.startKey);
        return result ? assertText(result) : null;
    }

    /*
     * Get the next text node.
     */

    get nextText(): Text | null {
        return this.endKey ? this.document.getNextText(this.endKey) : null;
    }

    /*
     * Get the previous text node.
     */
    get previousText(): Text | null {
        return this.startKey ? this.document.getPreviousText(this.startKey) : null;
    }

    /*
     * Get the marks of the current selection.
     */
    get marks(): ISet<Mark> {
        if (isBlockRangeSelection(this.selection)) {
            return ISet.create();
        }
        return this.isSelectionUnset
            ? ISet.create()
            : this.selection.marks || this.document.getMarksAtRange(this.selection);
    }

    /*
     * Get the active marks of the current selection.
     */
    get activeMarks(): ISet<Mark> {
        if (isBlockRangeSelection(this.selection)) {
            return ISet.create();
        }
        return this.isSelectionUnset
            ? ISet.create()
            : this.selection.marks || this.document.getActiveMarksAtRange(this.selection);
    }

    get blockSelection(): BlockRange {
        if (isBlockRangeSelection(this.selection)) {
            return this.selection;
        }
        throw new Error(`The selection is not a BlockRange so we can't get a blockSelection`);
    }

    /*
     * Get the block nodes in the current selection.
     */
    get blocks(): List<Block> {
        if (isBlockRangeSelection(this.selection)) {
            return this.selection.getBlocks(this.document);
        }
        return this.isSelectionUnset
            ? List.create()
            : this.document.getBlocksAtRange(this.selection);
    }

    /*
     * Get the fragment of the current selection.
     */
    get fragment(): Document {
        if (isBlockRangeSelection(this.selection)) {
            return Document.create({
                nodes: this.selection.getBlocks(this.document),
            });
        }
        return this.isSelectionUnset
            ? Document.create()
            : this.document.getFragmentAtRange(this.selection);
    }

    /*
     * Get the inline nodes in the current selection.
     */
    get inlines(): List<Inline> {
        if (isBlockRangeSelection(this.selection)) {
            return List.create();
        }

        return this.isSelectionUnset
            ? List.create()
            : this.document.getInlinesAtRange(this.selection);
    }

    get inlinesWithFragments(): List<Inline> {
        if (isBlockRangeSelection(this.selection)) {
            return List.create();
        }

        return this.isSelectionUnset
            ? List.create()
            : this.document.getInlinesAtRange(this.selection, true);
    }

    /*
     * Get the text nodes in the current selection.
     */
    get texts(): List<Text> {
        return this.isSelectionUnset
            ? List.create()
            : this.document.getTextsAtRange(this.selection);
    }

    /*
     * Check whether the selection is empty.
     */
    get isEmpty(): boolean {
        if (this.isCollapsed) {
            return true;
        }
        if (this.endOffset !== 0 && this.startOffset !== 0) {
            return false;
        }
        return this.fragment ? this.fragment.isEmpty : true;
    }

    /**
     * Check whether the selection is collapsed in a void node.
     */
    get isInVoid(): boolean {
        if (isBlockRangeSelection(this.selection) || this.isSelectionUnset || this.isExpanded) {
            return false;
        }
        return this.startKey ? this.document.hasVoidParent(this.startKey) : false;
    }

    /**
     * Check whether the selection is collapsed in a void block.
     */
    get isInVoidBlock(): boolean {
        if (
            isBlockRangeSelection(this.selection) ||
            this.isSelectionUnset ||
            this.isExpanded ||
            !this.startKey
        ) {
            return false;
        }

        const voidParent = this.document.getClosestVoid(this.startKey);
        return !!voidParent && voidParent.object === 'block';
    }

    /**
     * Create a `Value` from properties.
     */
    public static create(
        object: ValueCreateProps = {},
        options: {
            normalize?: boolean;
        } = {}
    ): Value {
        if (Value.isValue(object)) {
            return object;
        }

        let {
            document = Document.create(),
            selection = TextRange.create(),
            schema = Schema.create(),
            history = History.create(),
        } = object;

        if (isTextRangeSelection(selection) && selection.isUnset) {
            const text = document.getFirstText();
            if (text) {
                selection = selection.collapseToStartOf(text);
            }
        }

        let value = Value.createRecord({
            document,
            selection,
            // Combine the core schema with the custom one
            schema: CORE_SCHEMA.combineWith(schema),
            history,
        });

        if (options.normalize !== false) {
            value = value.change({ save: false }).normalize().value;
        }

        return value;
    }

    /*
     * Check if a `value` is a `Value`.
     */
    public static isValue(value: any): value is Value {
        return !!value?.[MODEL_TYPES.VALUE];
    }

    /*
     * Create a new `Change` with the current value as a starting point.
     */
    public change(attrs: ChangeFlags = {}): Change {
        return new Change({ ...attrs, value: this });
    }

    /*
     * Set the schema for this value.
     */
    public setSchema(schema: Schema | Schema[]): Value {
        return this.merge({
            schema: CORE_SCHEMA.combineWith(schema),
        });
    }

    /**
     * True if selection contains the given inline
     */
    public hasInline(type: string, withFragments = false): boolean {
        const inlines = withFragments ? this.inlinesWithFragments : this.inlines;
        return inlines.some((inline) => inline.type === type);
    }

    /**
     * True if selection contains the given block
     */
    public hasBlock(type: string): boolean {
        return this.blocks.some((block) => block.type === type);
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Value.prototype[MODEL_TYPES.VALUE] = true;
